import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { CompanyProjectsListings } from '@/api/v4/organization-projects-listings.api';
import { fetchCompanyProjectListings } from '@/api/v4/organization-projects-listings.api';
import { fetchSpotlightCompanyProjectListings } from '@/api/v4/spotlight.api';
import { QueryKey } from '@/config/query-client';

import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '@/features/companies/overview/company-fetch-context';

const companyProjectListingsFetchers: Record<
  CompanyFetchMode,
  (organizationId: string) => ReturnType<typeof fetchCompanyProjectListings>
> = {
  general: organizationId => fetchCompanyProjectListings({ organizationId }),
  spotlight: fetchSpotlightCompanyProjectListings,
};

export const useCompanyProjectsListings = (
  organizationId: string | null,
  options?: Omit<
    UseQueryOptions<
      CompanyProjectsListings,
      unknown,
      CompanyProjectsListings,
      (CompanyFetchMode | string | null)[]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  const fetchMode = useCompanyFetchMode();

  return useQuery({
    queryKey: [QueryKey.CompanyProjectsListings, fetchMode, organizationId],
    queryFn: () => {
      if (!organizationId) {
        return {
          listings: [],
          otherProjectsCount: 0,
        };
      }

      return companyProjectListingsFetchers[fetchMode](organizationId);
    },
    enabled: !!organizationId,
    ...options,
  });
};
